import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Eye, Copy, Users, ArrowRight, Globe, ShieldCheck, MagnifyingGlass, X, Star } from "@phosphor-icons/react";
import MySquads from "./MySquads";

const SORTS = [
  { id: "trending", label: "Trending" },
  { id: "most-copied", label: "Most Copied" },
  { id: "top-rated", label: "Top Rated" },
  { id: "newest", label: "Newest" },
];

function PublicPlansList() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [query, setQuery] = useState("");
  const [level, setLevel] = useState("All");
  const [type, setType] = useState("All");
  const [sort, setSort] = useState("trending");

  useEffect(() => {
    api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  const filtered = useMemo(() => {
    if (!plans) return null;
    const q = query.trim().toLowerCase();
    let list = plans.filter((p) => {
      if (level !== "All" && (p.level || "") !== level) return false;
      if (type !== "All" && (p.type || "") !== type) return false;
      if (!q) return true;
      const hay = [p.name, p.goal, p.level, p.type, p.owner_name].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
    if (sort === "most-copied") list = [...list].sort((a, b) => (b.copy_count || 0) - (a.copy_count || 0));
    else if (sort === "top-rated") list = [...list].sort((a, b) => {
      // Plans with zero reviews drop to the bottom regardless of avg.
      const ra = (a.review_count || 0) > 0 ? (a.avg_rating || 0) : -1;
      const rb = (b.review_count || 0) > 0 ? (b.avg_rating || 0) : -1;
      if (rb !== ra) return rb - ra;
      return (b.review_count || 0) - (a.review_count || 0);
    });
    else if (sort === "newest") list = [...list].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    else list = [...list].sort((a, b) => ((b.copy_count || 0) * 2 + (b.view_count || 0)) - ((a.copy_count || 0) * 2 + (a.view_count || 0)));
    return list;
  }, [plans, query, level, type, sort]);

  if (plans === null) return <div className="label-mono animate-pulse">LOADING...</div>;

  if (plans.length === 0) {
    return (
      <div className="border border-dashed border-border p-12 text-center">
        <div className="label-mono mb-3">NO PUBLIC PLANS YET</div>
        <p className="text-muted-foreground mb-6">Be the first — build a plan and toggle it public.</p>
        <Link to={user ? "/builder" : "/register"} className="bg-volt text-black px-5 py-3 font-display uppercase tracking-widest text-sm hover:bg-white">
          {user ? "Build & Share" : "Get Started"}
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="border border-volt/40 bg-volt/5 px-3 py-2 max-w-2xl mb-6 text-xs flex items-center gap-2" data-testid="community-tune-hint">
        <span className="label-mono text-volt shrink-0">// AUTO-TUNED</span>
        <span className="text-muted-foreground">Copying any plan rescales main-lift weights to <Link to="/assessment" className="text-volt hover:underline">your 1RMs</Link> automatically.</span>
      </div>

      {/* Search + filters */}
      <div className="tactical-card p-4 mb-6" data-testid="community-search-bar">
        <div className="flex items-center gap-2 mb-3">
          <MagnifyingGlass size={18} weight="bold" className="text-muted-foreground shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, goal, or operator..."
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
            data-testid="community-search-input"
          />
          {query && (
            <button onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground" data-testid="community-search-clear">
              <X size={14} weight="bold" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {["All", "microcycle", "mesocycle"].map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`px-3 py-1.5 border uppercase tracking-widest ${type === t ? "border-volt text-volt bg-volt/10" : "border-border hover:border-volt/50"}`}
              data-testid={`community-filter-type-${t}`}
            >
              {t === "All" ? "All Types" : t}
            </button>
          ))}
          <div className="w-px bg-border mx-1" />
          {["All", "Beginner", "Intermediate", "Advanced"].map((l) => (
            <button
              key={l}
              onClick={() => setLevel(l)}
              className={`px-3 py-1.5 border uppercase tracking-widest ${level === l ? "border-volt text-volt bg-volt/10" : "border-border hover:border-volt/50"}`}
              data-testid={`community-filter-level-${l}`}
            >
              {l}
            </button>
          ))}
          <div className="w-px bg-border mx-1" />
          {SORTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSort(s.id)}
              className={`px-3 py-1.5 border uppercase tracking-widest ${sort === s.id ? "border-volt text-volt bg-volt/10" : "border-border hover:border-volt/50"}`}
              data-testid={`community-sort-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="label-mono text-muted-foreground mt-3" data-testid="community-result-count">
          SHOWING {filtered.length} / {plans.length} PLANS
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground text-sm" data-testid="community-no-matches">
          No plans match your search. Try clearing a filter.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <Link to={`/p/${p.share_slug}`} key={p.id} className="tactical-card p-5 flex flex-col h-full" data-testid={`community-card-${p.share_slug}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="label-mono text-volt">{p.type === "mesocycle" ? "MESOCYCLE" : "MICROCYCLE"}</div>
                <div className="label-mono">// {p.owner_name?.toUpperCase() || "OPERATOR"}</div>
              </div>
              <h3 className="font-display text-2xl font-black leading-tight mb-2">{p.name}</h3>
              <div className="text-xs text-muted-foreground mb-4 space-y-1">
                {p.goal && <div>GOAL: {p.goal}</div>}
                {p.level && <div>LEVEL: {p.level}</div>}
              </div>
              <div className="mt-auto flex items-center justify-between text-xs">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Eye size={12} weight="bold" /> {p.view_count || 0}</span>
                  <span className="inline-flex items-center gap-1 text-volt"><Copy size={12} weight="bold" /> {p.copy_count || 0}</span>
                  {(p.review_count || 0) > 0 && (
                    <span className="inline-flex items-center gap-1" data-testid={`community-rating-${p.share_slug}`}>
                      <Star size={12} weight="fill" className="text-volt" />
                      {Number(p.avg_rating || 0).toFixed(1)}
                      <span className="text-muted-foreground/70">({p.review_count})</span>
                    </span>
                  )}
                </div>
                <div className="inline-flex items-center gap-1 label-mono hover:text-volt">View <ArrowRight size={12} weight="bold" /></div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

export default function Community() {
  const { user } = useAuth();
  const [tab, setTab] = useState(user ? "squads" : "public");

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3" data-testid="community-brand">
            <div className="w-9 h-9 grid place-items-center bg-volt text-black font-display font-black text-base">TP</div>
            <div className="leading-none">
              <div className="font-display text-2xl font-black tracking-tight">TACTICAL</div>
              <div className="label-mono">PROGRESSION</div>
            </div>
          </Link>
          <div className="flex gap-2">
            {user ? (
              <Link to="/dashboard" className="border border-border px-4 py-2 uppercase text-xs tracking-widest hover:border-volt hover:text-volt" data-testid="community-dashboard-link">Dashboard</Link>
            ) : (
              <Link to="/login" className="border border-border px-4 py-2 uppercase text-xs tracking-widest hover:border-volt hover:text-volt">Login</Link>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2 label-mono text-volt">
          <Users size={14} weight="bold" /> // OPERATOR FEED
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mb-3">COMMUNITY</h1>
        <p className="text-muted-foreground max-w-2xl mb-6">
          Browse public plans shared by operators worldwide, or run private squads with your friends, family, or unit.
        </p>

        {/* Tabs */}
        <div className="flex border-b border-border mb-8" data-testid="community-tabs">
          {user && (
            <button
              onClick={() => setTab("squads")}
              data-testid="tab-squads"
              className={`px-4 py-3 font-display uppercase tracking-widest text-sm inline-flex items-center gap-2 -mb-px border-b-2 ${tab === "squads" ? "border-volt text-volt" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              <ShieldCheck size={16} weight="bold" /> My Squads
            </button>
          )}
          <button
            onClick={() => setTab("public")}
            data-testid="tab-public"
            className={`px-4 py-3 font-display uppercase tracking-widest text-sm inline-flex items-center gap-2 -mb-px border-b-2 ${tab === "public" ? "border-volt text-volt" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <Globe size={16} weight="bold" /> Public Plans
          </button>
        </div>

        {tab === "squads" && user ? <MySquads /> : <PublicPlansList />}
      </main>
      <footer className="border-t border-border py-4 text-center label-mono">TACTICAL PROGRESSION // FM 7-22 H2F DOCTRINE</footer>
    </div>
  );
}
